import { useSetUserQuota } from "@vidario/api-client";
import { countLabel, SEARCH_NOUN, type User } from "@vidario/domain";
import { Button, Input, useToast } from "@vidario/ui";
import { useState, type ReactElement } from "react";

/** Per-user search-quota editor for the Nutzerverwaltung table. Admins are
 * unlimited and never consume, so their row shows „unbegrenzt" instead of an
 * editable field. Recruiters get an editable „Zugeteilt" input plus a
 * compact read-out of consumed / remaining searches.
 *
 * Fields are coalesced to 0 so the cell renders sensibly against a backend
 * that hasn't yet migrated the quota columns (they arrive as `undefined`). */
export function QuotaCell({ user }: { user: User }): ReactElement {
  const setQuota = useSetUserQuota();
  const toast = useToast();

  const quota = user.searchQuota ?? 0;
  const used = user.searchesUsed ?? 0;
  const remaining = user.searchesRemaining ?? Math.max(0, quota - used);

  const [draft, setDraft] = useState<string>(String(quota));

  if (user.role === "admin") {
    return <span className="text-text-tertiary">unbegrenzt</span>;
  }

  const parsed = Number(draft);
  const valid = Number.isInteger(parsed) && parsed >= 0;
  const dirty = valid && parsed !== quota;

  const save = async (): Promise<void> => {
    if (!dirty) return;
    try {
      await setQuota.mutateAsync({ id: user.id, searchQuota: parsed });
      toast.push({
        kind: "success",
        title: "Kontingent gespeichert",
        message: `${user.name}: ${countLabel(parsed, SEARCH_NOUN)} zugeteilt.`,
      });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Speichern fehlgeschlagen",
        message: err instanceof Error ? err.message : "Unbekannter Fehler.",
      });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min={0}
        inputSize="md"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
        }}
        className="!h-9 w-16 tabular-nums"
        aria-label={`Such-Kontingent für ${user.name}`}
      />
      {dirty ? (
        <Button size="sm" onClick={() => void save()} loading={setQuota.isPending}>
          Speichern
        </Button>
      ) : (
        <span
          className="whitespace-nowrap text-xs text-text-tertiary"
          title={`${used} verbraucht · ${remaining} übrig`}
        >
          {used}&nbsp;verbr. · {remaining}&nbsp;übrig
        </span>
      )}
    </div>
  );
}
